import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  CopyProps,
  UtilityCopy,
  UtilityCopyProps,
} from "@bluebottlecoffee/design-system";
import { BaseProps, filters } from "../../../../lib/sanity/shared";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../../lib/utils";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { HelpPage as HelpPageSchema } from "../../../../lib/sanity-schema";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "../../../../lib/sanity/translation-group-queries";
import { toUtilityCopyProps } from "../../../../lib/transformers/utility-copy";

interface PageData extends BaseProps {
  helpPage: HelpPageSchema;
  helpTranslations: CopyProps;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const { lang } = defaultProps;

  const query = buildTranslationGroupQuery(lang, "UtilityPage");

  const helpTranslations = await sanityClient()
    .fetch<TranslationGroupSanityType>(query)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<CopyProps>(lang, translations),
    );

  const helpPage = await sanityClient().fetch<HelpPageSchema>(`*[
    ${filters.whereType("helpPage")} &&
    ${filters.excludeDrafts}
  ][0]`);

  return {
    props: {
      helpPage,
      helpTranslations,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: HelpPageSchema) {
  if (!data.title) {
    throw Error("Help Page must have a title.");
  }
}

const HelpPage: FunctionComponent<PageData> = ({
  helpPage,
  helpTranslations,
  ...layoutWrapperProps
}) => {
  validate(helpPage);

  const { region, lang } = layoutWrapperProps;

  const utilityCopyProps: UtilityCopyProps = {
    ...toUtilityCopyProps(helpPage.copy, region, lang),
    copyProps: helpTranslations,
  };

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: helpPage.title[lang],
        metaTags: helpPage.metaTags,
      }}
    >
      {helpPage.copy && <UtilityCopy {...utilityCopyProps} />}
      {helpPage.modules?.length > 0 &&
        helpPage.modules.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default HelpPage;
